import { formatCredits } from './credits';

/**
 * The words for the wizard's short waits (steps 1–3b, the balance check and
 * the quote comparison): what Robbie is on, the usual range, and the done
 * line. One place, so the overlay, the Robbie panel, the StepBar and the
 * toast never describe one wait two ways. Never elapsed time.
 *
 * Every range scales with what the wait is working through (files,
 * components, suppliers), and the overlay turns to "taking a bit longer"
 * only past the top of that range — counted from the server's own start time
 * where the API gives one, so a reload neither resets nor shortens it.
 */

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** "a.xlsx and b.pdf", "a, b and c". */
export const joinNames = (names: string[]): string =>
  names.length <= 1 ? names[0] ?? '' : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** The usual span of one wait, in ms. */
export interface WaitRange {
  lowMs: number;
  highMs: number;
}

const SECOND = 1_000;
const MINUTE = 60_000;

/** A base plus a per-item cost, at least one item. */
const rangeFor = (items: number, low: [number, number], high: [number, number]): WaitRange => {
  const n = Math.max(1, Math.floor(items) || 1);
  return { lowMs: (low[0] + low[1] * n) * SECOND, highMs: (high[0] + high[1] * n) * SECOND };
};

const roundTo5 = (seconds: number) => Math.max(5, Math.round(seconds / 5) * 5);

/** "Usually 15–50 seconds", "Usually 1–3 minutes", "Usually about 2 minutes". */
export const usualLabel = ({ lowMs, highMs }: WaitRange): string => {
  if (highMs <= 90 * SECOND) {
    const low = roundTo5(lowMs / SECOND);
    const high = Math.max(low, roundTo5(highMs / SECOND));
    return low === high ? `Usually about ${high} seconds` : `Usually ${low}–${high} seconds`;
  }
  const low = Math.max(1, Math.round(lowMs / MINUTE));
  const high = Math.max(low, Math.ceil(highMs / MINUTE));
  return low === high ? `Usually about ${plural(high, 'minute')}` : `Usually ${low}–${high} minutes`;
};

/** Reading the BOM files: ~15–50 s for one file, more for each one after. */
export const extractionRange = (files: number) => rangeFor(files, [5, 10], [20, 30]);
/** Mapping and context questions: a few seconds to start plus each component. */
export const analysisRange = (components: number) => rangeFor(components, [5, 2], [10, 5]);
/** The validate-and-recommend check: one model call that grows with the BOM. */
export const validationRange = (components: number) => rangeFor(components, [20, 1], [40, 3]);
/** The award recommendation reads every quote. */
export const quotesRange = (suppliers: number) => rangeFor(suppliers, [15, 2], [30, 5]);
/** Rate + balance: two small reads. */
export const creditsCheckRange: WaitRange = { lowMs: 1 * SECOND, highMs: 6 * SECOND };

export const extractionCopy = {
  title: (files: number) => `Reading your ${plural(files, 'file')}`,
  usual: (files: number) => usualLabel(extractionRange(files)),
  longAfterMs: (files: number) => extractionRange(files).highMs,
  panel: (names: string[]) => `Reading ${joinNames(names)}.`,
  failedTitle: "Couldn't read your files",
  failedMessage: 'Nothing saved. No credits used.',
};

const hasTargetPrice = (c: { targetPrice?: unknown }) => typeof c?.targetPrice === 'number' && c.targetPrice > 0;

/** The toast once extraction lands on step 2. */
export const extractionDoneToast = (components: Array<{ targetPrice?: unknown }>, fileNames: string[]) => {
  const missing = components.filter((c) => !hasTargetPrice(c)).length;
  return {
    title: `Found ${plural(components.length, 'component')}${missing > 0 ? ` · ${missing} missing a target price` : ''}`,
    message: fileNames.length > 0 ? `From ${joinNames(fileNames)}` : undefined,
  };
};

export const creditsCheckCopy = {
  title: 'Checking your balance',
  usual: 'Usually a second or two',
  longAfterMs: creditsCheckRange.highMs,
  panel: 'Checking your balance.',
};

export const analysisCopy = {
  title: (n: number) => `Going through ${plural(n, 'component')}`,
  sub: 'Working out which specs I still need from you',
  usual: (n: number) => usualLabel(analysisRange(n)),
  longAfterMs: (n: number) => analysisRange(n).highMs,
  panel: (n: number) => `Going through your ${plural(n, 'component')}.`,
  failedTitle: "Analysis didn't finish",
  failedMessage: 'Your BOM and credits are untouched.',
  retry: 'Run the analysis again',
};

export const validationCopy = {
  title: (n: number) => `Checking ${plural(n, 'component')}`,
  sub: 'Completeness, quantities and spec gaps — then better parts where I find any',
  usual: (n: number) => usualLabel(validationRange(n)),
  longAfterMs: (n: number) => validationRange(n).highMs,
  panel: (n: number) => `Checking your ${plural(n, 'component')}.`,
  failedTitle: "Check didn't finish",
  failedMessage: 'Nothing in your BOM changed.',
  keep: 'Keep the last results',
  retry: 'Check again',
  skip: 'Continue without the check',
};

export const quotesCopy = {
  usual: (suppliers: number) => usualLabel(quotesRange(suppliers)),
  longAfterMs: (suppliers: number) => quotesRange(suppliers).highMs,
};

export const discoveryStartedToast = (credits: number | null, components: number, balance: number | null) => {
  const used =
    credits === null
      ? `Started discovery for ${plural(components, 'component')}`
      : `${formatCredits(credits)} credits used for ${plural(components, 'component')}`;
  return { title: balance === null ? used : `${used} · balance ${formatCredits(balance)}` };
};

export { plural };
